import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { ArrowLeft, Calendar, Clock, User, ExternalLink, Calculator, Brain, Trophy, CheckCircle, Sparkles, Target, Zap, BookOpen, Award, TrendingUp, Lightbulb, Code, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavigationButtons from '@/components/NavigationButtons';
import RelatedLinks from '@/components/RelatedLinks';
import { WaitlistDialog } from '@/components/WaitlistDialog';
import heroImage from '@/assets/deepseek-math-v2-hero.jpg';

const DeepseekMathV2 = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": "https://deepseek.ai/blog/deepseek-math-v2#article",
        "headline": "DeepSeekMath-V2: Revolutionary Self-Verifiable Mathematical Reasoning AI",
        "description": "DeepSeekMath-V2 achieves gold-level scores on IMO 2025, CMO 2024, and near-perfect 118/120 on Putnam 2024. Discover how DeepSeek's 685B parameter model revolutionizes theorem proving with self-verifiable mathematical reasoning.",
        "datePublished": "2025-11-30",
        "dateModified": "2025-11-30",
        "author": {
          "@type": "Organization",
          "name": "The Independent DeepSeek Guide"
        },
        "publisher": {
          "@type": "Organization",
          "name": "The Independent DeepSeek Guide",
          "logo": {
            "@type": "ImageObject",
            "url": "https://deepseek.ai/favicon-ds.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://deepseek.ai/blog/deepseek-math-v2"
        },
        "image": "https://deepseek.ai/og-image.png",
        "articleSection": "AI Technology",
        "keywords": "DeepSeekMath-V2, DeepSeek Math, mathematical reasoning AI, theorem proving, IMO 2025, Putnam 2024, CMO 2024, self-verifiable reasoning, DeepSeek AI, mathematical AI"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://deepseek.ai"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://deepseek.ai/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "DeepSeekMath-V2",
            "item": "https://deepseek.ai/blog/deepseek-math-v2"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is DeepSeekMath-V2?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DeepSeekMath-V2 is a 685 billion parameter AI model developed by DeepSeek that specializes in self-verifiable mathematical reasoning. It achieves gold-level scores on IMO 2025, CMO 2024, and near-perfect 118/120 on Putnam 2024."
            }
          },
          {
            "@type": "Question",
            "name": "What makes DeepSeekMath-V2 different from other math AI models?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Unlike models that just optimize for correct final answers, DeepSeekMath-V2 focuses on self-verifiable reasoning - it can verify the comprehensiveness and rigor of its mathematical proofs, making it suitable for theorem proving where step-by-step derivation is required."
            }
          },
          {
            "@type": "Question",
            "name": "What competitions has DeepSeekMath-V2 performed well on?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DeepSeekMath-V2 achieved gold-level scores on IMO 2025 and CMO 2024, and scored a near-perfect 118/120 on Putnam 2024, demonstrating exceptional theorem-proving capabilities."
            }
          },
          {
            "@type": "Question",
            "name": "Is DeepSeekMath-V2 open source?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, DeepSeekMath-V2 is released under the Apache License 2.0, making it available for research and commercial use."
            }
          },
          {
            "@type": "Question",
            "name": "How many parameters does DeepSeekMath-V2 have?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "DeepSeekMath-V2 has 685 billion parameters and is built on top of DeepSeek-V3.2-Exp-Base, making it one of the largest specialized mathematical reasoning models available."
            }
          },
          {
            "@type": "Question",
            "name": "What is the IMO-ProofBench?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "IMO-ProofBench is a benchmark developed by the DeepMind team behind DeepThink IMO-Gold. It evaluates AI models on their ability to generate rigorous mathematical proofs for International Mathematical Olympiad problems."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <Head>
        <title>DeepSeekMath-V2 — IMO Gold Math Reasoning AI</title>
        <meta name="description" content="DeepSeekMath-V2 reaches IMO 2025 gold-level scores and 118/120 on Putnam 2024 with self-verifiable mathematical reasoning." />
        <meta name="keywords" content="DeepSeekMath-V2, DeepSeek Math V2, DeepSeek mathematical reasoning, theorem proving AI, IMO 2025 AI, Putnam 2024 AI, CMO 2024, self-verifiable reasoning, DeepSeek AI, mathematical proof AI, LLM math, DeepSeek 685B, mathematical olympiad AI, AI theorem prover" />
        <link rel="canonical" href="https://deepseek.ai/blog/deepseek-math-v2" />
        
        <meta property="og:title" content="DeepSeekMath-V2: Revolutionary Self-Verifiable Mathematical Reasoning AI" />
        <meta property="og:description" content="DeepSeekMath-V2 achieves gold-level scores on IMO 2025 and near-perfect 118/120 on Putnam 2024. Discover DeepSeek's breakthrough in mathematical AI." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/blog/deepseek-math-v2" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:site_name" content="DeepSeek AI Fan Site" />
        <meta property="article:published_time" content="2025-11-30" />
        <meta property="article:section" content="AI Technology" />
        <meta property="article:tag" content="DeepSeek" />
        <meta property="article:tag" content="Mathematical AI" />
        <meta property="article:tag" content="Theorem Proving" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeekMath-V2: Self-Verifiable Mathematical Reasoning AI" />
        <meta name="twitter:description" content="Gold-level IMO 2025, 118/120 Putnam 2024. DeepSeek's 685B parameter breakthrough in mathematical reasoning." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Breadcrumbs */}
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li><a href="/" className="text-blue-600 hover:underline">Home</a></li>
              <li><span className="text-gray-400">/</span></li>
              <li><a href="/blog" className="text-blue-600 hover:underline">Blog</a></li>
              <li><span className="text-gray-400">/</span></li>
              <li className="text-gray-600">DeepSeekMath-V2</li>
            </ol>
          </nav>

          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-6 hover:bg-blue-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          <NavigationButtons />

          <article className="prose prose-lg max-w-none">
            {/* Hero Image */}
            <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={heroImage} 
                alt="DeepSeekMath-V2 AI Mathematical Reasoning - Neural network visualization with mathematical patterns" 
                className="w-full h-64 md:h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-blue-600 rounded-full text-sm font-medium">
                    AI Technology
                  </span>
                  <span className="px-3 py-1 bg-amber-500 rounded-full text-sm font-medium flex items-center gap-1">
                    <Trophy className="h-3 w-3" />
                    IMO 2025 Gold
                  </span>
                  <span className="px-3 py-1 bg-green-600 rounded-full text-sm font-medium">
                    Open Source
                  </span>
                </div>
                <p className="text-sm opacity-90">Published November 30, 2025 • 12 min read</p>
              </div>
            </div>

            <header className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                DeepSeekMath-V2: Revolutionary Self-Verifiable Mathematical Reasoning AI
              </h1>
              
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                DeepSeek has released DeepSeekMath-V2, a groundbreaking 685 billion parameter AI model that achieves gold-level scores on IMO 2025, CMO 2024, and an unprecedented 118/120 on Putnam 2024. This isn't just another language model that can do math—it's a fundamental shift in how AI approaches mathematical reasoning, emphasizing <strong>self-verifiable proofs</strong> over mere answer accuracy.
              </p>
              
              <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm border-b border-gray-200 pb-6">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  November 30, 2025
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  12 min read
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  DeepSeek AI Fan Site
                </span>
              </div>
            </header>

            {/* Key Takeaways Box */}
            <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-0 shadow-xl">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
                  <Sparkles className="h-5 w-5" />
                  Key Takeaways
                </h2>
                <ul className="space-y-3 text-blue-50">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-green-300" />
                    <span><strong className="text-white">Gold-level performance</strong> on IMO 2025 and CMO 2024, plus 118/120 on Putnam 2024</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-green-300" />
                    <span><strong className="text-white">685 billion parameters</strong> built on DeepSeek-V3.2-Exp-Base architecture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-green-300" />
                    <span><strong className="text-white">Self-verifiable reasoning:</strong> Model verifies its own proofs before finalizing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-green-300" />
                    <span><strong className="text-white">Apache 2.0 license</strong> - fully open source for research and commercial use</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Table of Contents */}
            <Card className="mb-8 bg-gradient-to-r from-gray-50 to-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  Table of Contents
                </h2>
                <nav>
                  <ul className="space-y-2 text-blue-700">
                    <li><a href="#introduction" className="hover:underline flex items-center gap-2"><span className="text-gray-400">1.</span> Introduction: Why Mathematical Reasoning Matters</a></li>
                    <li><a href="#problem" className="hover:underline flex items-center gap-2"><span className="text-gray-400">2.</span> The Problem with Current Math AI</a></li>
                    <li><a href="#self-verification" className="hover:underline flex items-center gap-2"><span className="text-gray-400">3.</span> The Self-Verification Breakthrough</a></li>
                    <li><a href="#training" className="hover:underline flex items-center gap-2"><span className="text-gray-400">4.</span> How DeepSeekMath-V2 Was Trained</a></li>
                    <li><a href="#competition-results" className="hover:underline flex items-center gap-2"><span className="text-gray-400">5.</span> Competition Results & Benchmarks</a></li>
                    <li><a href="#technical-architecture" className="hover:underline flex items-center gap-2"><span className="text-gray-400">6.</span> Technical Architecture</a></li>
                    <li><a href="#implications" className="hover:underline flex items-center gap-2"><span className="text-gray-400">7.</span> Real-World Implications</a></li>
                    <li><a href="#comparison" className="hover:underline flex items-center gap-2"><span className="text-gray-400">8.</span> Comparison with Other Math AI Models</a></li>
                    <li><a href="#getting-started" className="hover:underline flex items-center gap-2"><span className="text-gray-400">9.</span> Getting Started with DeepSeekMath-V2</a></li>
                    <li><a href="#faq" className="hover:underline flex items-center gap-2"><span className="text-gray-400">10.</span> Frequently Asked Questions</a></li>
                  </ul>
                </nav>
              </CardContent>
            </Card>

            {/* Key Highlights Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 text-center">
                  <Trophy className="h-10 w-10 text-amber-600 mx-auto mb-2" />
                  <p className="font-bold text-gray-900 text-lg">IMO 2025</p>
                  <p className="text-amber-700 font-semibold">Gold Level</p>
                  <p className="text-xs text-gray-500 mt-1">International Math Olympiad</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 text-center">
                  <Award className="h-10 w-10 text-green-600 mx-auto mb-2" />
                  <p className="font-bold text-gray-900 text-lg">Putnam 2024</p>
                  <p className="text-green-700 font-semibold">118/120</p>
                  <p className="text-xs text-gray-500 mt-1">98.3% accuracy</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 text-center">
                  <Brain className="h-10 w-10 text-blue-600 mx-auto mb-2" />
                  <p className="font-bold text-gray-900 text-lg">685B</p>
                  <p className="text-blue-700 font-semibold">Parameters</p>
                  <p className="text-xs text-gray-500 mt-1">Massive scale</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-4 text-center">
                  <Code className="h-10 w-10 text-purple-600 mx-auto mb-2" />
                  <p className="font-bold text-gray-900 text-lg">Apache 2.0</p>
                  <p className="text-purple-700 font-semibold">Open Source</p>
                  <p className="text-xs text-gray-500 mt-1">Free to use</p>
                </CardContent>
              </Card>
            </div>

            <section id="introduction">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">1</span>
                Introduction: Why Mathematical Reasoning Matters
              </h2>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                Mathematics has long served as the ultimate test for artificial intelligence. Unlike natural language tasks where ambiguity is tolerated and multiple answers can be "correct enough," mathematics demands precision. A proof is either valid or it isn't. An answer is either right or wrong. This binary nature makes mathematical reasoning one of the most challenging and important benchmarks for AI systems.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Over the past year, large language models have made remarkable progress in mathematical reasoning. Through reinforcement learning techniques that reward correct final answers, models improved from struggling with basic algebra to saturating quantitative reasoning competitions like AIME (American Invitational Mathematics Examination) and HMMT (Harvard-MIT Mathematics Tournament). This rapid improvement seemed to suggest that scaling up and better reward signals were all that was needed.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                But <strong>DeepSeek</strong> identified a fundamental flaw in this approach: <em>correct answers don't guarantee correct reasoning</em>. A model might arrive at the right numerical answer through flawed logic, lucky guessing, or pattern matching without true understanding. This becomes critically problematic for tasks like theorem proving, where the goal isn't just a final answer but a rigorous, step-by-step derivation that can be verified by mathematicians.
              </p>
              
              <Card className="my-6 bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <blockquote className="text-lg italic text-gray-700 border-l-4 border-blue-500 pl-4">
                    "To push the limits of deep reasoning, we believe it is necessary to verify the comprehensiveness and rigor of mathematical reasoning. Self-verification is particularly important for scaling test-time compute, especially for open problems without known solutions."
                    <footer className="text-sm text-gray-500 mt-2">— DeepSeekMath-V2 Research Team</footer>
                  </blockquote>
                </CardContent>
              </Card>
            </section>

            <section id="problem">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">2</span>
                The Problem with Current Math AI
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Traditional approaches to mathematical AI have relied heavily on reinforcement learning with outcome-based rewards. The training loop is simple: generate an answer, check if it matches the correct answer, and reward accordingly. While this approach has driven impressive gains on competition math problems, it has three fundamental limitations:
              </p>
              
              <div className="grid md:grid-cols-1 gap-4 my-6">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full flex-shrink-0">
                        <Target className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Problem 1: Correct Answers ≠ Correct Reasoning</h3>
                        <p className="text-gray-700">A model can get the right answer for the wrong reasons. It might recognize patterns from training data, make computational errors that cancel out, or simply guess well. In high-stakes applications like scientific research or formal verification, this is unacceptable.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full flex-shrink-0">
                        <Calculator className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Problem 2: Inapplicable to Theorem Proving</h3>
                        <p className="text-gray-700">Many mathematical tasks—especially theorem proving—require rigorous step-by-step derivation rather than numerical answers. You can't reward a proof based on whether the "final answer" is correct because proofs don't have final answers in the traditional sense. They have logical structures that must be verified.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full flex-shrink-0">
                        <Lightbulb className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Problem 3: Can't Scale to Open Problems</h3>
                        <p className="text-gray-700">For unsolved mathematical problems, there is no known answer to reward against. If we want AI to contribute to mathematical research—to prove new theorems and solve open conjectures—we need a system that can verify its own reasoning without external ground truth.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section id="self-verification">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">3</span>
                The Self-Verification Breakthrough
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>DeepSeekMath-V2</strong> introduces a paradigm shift: instead of optimizing for correct final answers, it optimizes for <em>self-verifiable mathematical reasoning</em>. The key insight is that mathematical proofs have an inherent structure that can be checked independently of knowing the final answer.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                The self-verification approach works as follows: the model doesn't just generate a proof—it also learns to verify that each step follows logically from previous steps, that no assumptions are made without justification, and that the overall argument is complete and rigorous. This creates a powerful feedback loop where the model can improve its reasoning by catching its own mistakes.
              </p>
              
              <Card className="my-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-300">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Why Self-Verification Changes Everything
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Rigorous proofs:</strong> Every step can be traced and verified, not just the final answer</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Applicable to theorem proving:</strong> Works for problems where there's no numerical "answer"</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Scales to open problems:</strong> Can work on unsolved problems by verifying reasoning without ground truth</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Self-improving:</strong> The model identifies and fixes its own mistakes before finalizing</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section id="training">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">4</span>
                How DeepSeekMath-V2 Was Trained
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                The training methodology for <strong>DeepSeekMath-V2</strong> represents a sophisticated multi-stage process that creates a virtuous cycle of improvement between proof generation and verification:
              </p>
              
              <div className="space-y-4 my-6">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">Stage 1: Train an LLM-Based Verifier</h3>
                    <p className="text-gray-700">First, DeepSeek trained an accurate and faithful verifier specifically for theorem proving. This verifier learns to assess whether a mathematical proof is valid—checking logical consistency, completeness, and rigor. The verifier is trained on a large corpus of human-verified mathematical proofs.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-indigo-500">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">Stage 2: Train the Proof Generator</h3>
                    <p className="text-gray-700">The proof generator is then trained using the verifier as its reward model. Instead of rewarding correct final answers, the generator is rewarded for producing proofs that pass the verifier's scrutiny. This fundamentally changes what the model optimizes for.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">Stage 3: Incentivize Self-Correction</h3>
                    <p className="text-gray-700">The generator is incentivized to identify and resolve as many issues as possible in its own proofs <em>before</em> finalizing them. This creates a model that doesn't just generate proofs but actively reviews and improves them—similar to how a human mathematician would draft, check, and revise a proof.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-pink-500">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">Stage 4: Scale Verification Compute</h3>
                    <p className="text-gray-700">As the generator becomes stronger, maintaining the generation-verification gap becomes crucial. DeepSeek scales verification compute to automatically label new hard-to-verify proofs, creating fresh training data to continuously improve the verifier. This prevents the generator from "outgrowing" its critic.</p>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section id="competition-results">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">5</span>
                Competition Results & Benchmarks
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>DeepSeekMath-V2</strong> has achieved remarkable results on the world's most prestigious mathematics competitions. These aren't just incremental improvements—they represent AI systems performing at the level of the world's best human mathematicians:
              </p>
              
              <Card className="mb-6 border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-amber-600" />
                    Mathematics Competition Performance
                  </h3>
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-amber-200 pb-4">
                      <div>
                        <span className="font-bold text-lg">IMO 2025</span>
                        <p className="text-sm text-gray-600">International Mathematical Olympiad</p>
                      </div>
                      <span className="bg-amber-500 text-white px-4 py-2 rounded-full font-bold mt-2 md:mt-0">🥇 Gold Level</span>
                    </div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-amber-200 pb-4">
                      <div>
                        <span className="font-bold text-lg">CMO 2024</span>
                        <p className="text-sm text-gray-600">China Mathematical Olympiad</p>
                      </div>
                      <span className="bg-amber-500 text-white px-4 py-2 rounded-full font-bold mt-2 md:mt-0">🥇 Gold Level</span>
                    </div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                      <div>
                        <span className="font-bold text-lg">Putnam 2024</span>
                        <p className="text-sm text-gray-600">William Lowell Putnam Mathematical Competition</p>
                      </div>
                      <span className="bg-green-500 text-white px-4 py-2 rounded-full font-bold mt-2 md:mt-0">118/120 (98.3%)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">IMO-ProofBench Results</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                The model was also evaluated on <strong>IMO-ProofBench</strong>, a benchmark developed by the DeepMind team behind the DeepThink IMO-Gold project. This benchmark specifically tests AI systems on their ability to generate rigorous mathematical proofs—not just numerical answers—for International Mathematical Olympiad problems.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                DeepSeekMath-V2 demonstrated strong theorem-proving capabilities on this benchmark, particularly when scaled test-time compute was applied. This means giving the model more time and computational resources to think through problems and verify its reasoning.
              </p>

              <Card className="my-6 bg-gray-50 border-gray-200">
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-3">Understanding the Competition Benchmarks</h4>
                  <div className="space-y-4 text-gray-700">
                    <div>
                      <strong>IMO (International Mathematical Olympiad):</strong> The world's most prestigious mathematics competition for pre-university students. Problems require creative mathematical thinking and rigorous proof construction. Gold medals are awarded to roughly the top 8% of contestants.
                    </div>
                    <div>
                      <strong>Putnam Competition:</strong> North America's most prestigious university-level mathematics competition. Known for its extremely challenging problems—the median score is typically 0 out of 120. Scoring 118/120 would place in the top handful of competitors worldwide.
                    </div>
                    <div>
                      <strong>CMO (China Mathematical Olympiad):</strong> China's national mathematical olympiad, used to select the Chinese IMO team. Problems are of comparable difficulty to the IMO.
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section id="technical-architecture">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">6</span>
                Technical Architecture
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>DeepSeekMath-V2</strong> is built with impressive technical specifications that enable its breakthrough performance:
              </p>
              
              <Card className="mb-6 bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300 shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Code className="h-5 w-5 text-gray-600" />
                    Model Specifications
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border-b border-gray-200 pb-3">
                        <p className="text-sm text-gray-500">Model Size</p>
                        <p className="text-2xl font-bold text-gray-900">685 Billion Parameters</p>
                      </div>
                      <div className="border-b border-gray-200 pb-3">
                        <p className="text-sm text-gray-500">Base Model</p>
                        <p className="text-lg font-bold text-gray-900">DeepSeek-V3.2-Exp-Base</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="border-b border-gray-200 pb-3">
                        <p className="text-sm text-gray-500">Tensor Types</p>
                        <p className="text-lg font-bold text-gray-900">BF16 / F8_E4M3 / F32</p>
                      </div>
                      <div className="border-b border-gray-200 pb-3">
                        <p className="text-sm text-gray-500">License</p>
                        <p className="text-lg font-bold text-gray-900">Apache 2.0 (Open Source)</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">Downloads Last Month</p>
                    <p className="text-xl font-bold text-gray-900">2,642+ downloads</p>
                  </div>
                </CardContent>
              </Card>
              
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">The Generation-Verification Gap</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                A key architectural innovation is the management of the "generation-verification gap." As the proof generator becomes stronger at producing valid proofs, it risks outpacing the verifier's ability to find errors. DeepSeek addresses this by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Continuously scaling verification compute to handle more complex proofs</li>
                <li>Automatically labeling new hard-to-verify proofs as training data</li>
                <li>Creating a dynamic training pipeline where both components improve together</li>
                <li>Using scaled test-time compute to allow deeper reasoning during inference</li>
              </ul>
            </section>

            <section id="implications">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">7</span>
                Real-World Implications
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                The implications of <strong>DeepSeekMath-V2</strong> extend far beyond competition mathematics. Self-verifiable mathematical reasoning could transform multiple fields:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 my-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Target className="h-5 w-5 text-blue-600" />
                      </div>
                      <h3 className="font-bold text-gray-900">Scientific Research</h3>
                    </div>
                    <p className="text-gray-700 text-sm">Rigorous mathematical reasoning could accelerate discoveries in theoretical physics, chemistry, biology, and other sciences where mathematical proofs underpin major advances.</p>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <h3 className="font-bold text-gray-900">Formal Verification</h3>
                    </div>
                    <p className="text-gray-700 text-sm">Self-verifiable proofs could revolutionize software and hardware verification, helping prove the correctness of critical systems in aerospace, automotive, and financial industries.</p>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <GraduationCap className="h-5 w-5 text-purple-600" />
                      </div>
                      <h3 className="font-bold text-gray-900">Education</h3>
                    </div>
                    <p className="text-gray-700 text-sm">AI tutors that can explain mathematical reasoning step-by-step with verified correctness could transform mathematics education at all levels.</p>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Lightbulb className="h-5 w-5 text-amber-600" />
                      </div>
                      <h3 className="font-bold text-gray-900">Open Problem Solving</h3>
                    </div>
                    <p className="text-gray-700 text-sm">Self-verification enables meaningful progress on unsolved mathematical problems like the Millennium Prize Problems, where no ground truth exists.</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="my-6 bg-indigo-50 border-indigo-200">
                <CardContent className="p-6">
                  <blockquote className="text-lg italic text-gray-700 border-l-4 border-indigo-500 pl-4">
                    "While much work remains, these results suggest that self-verifiable mathematical reasoning is a feasible research direction that may help develop more capable mathematical AI systems."
                    <footer className="text-sm text-gray-500 mt-2">— DeepSeekMath-V2 Research Paper</footer>
                  </blockquote>
                </CardContent>
              </Card>
            </section>

            <section id="comparison">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">8</span>
                Comparison with Other Math AI Models
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                How does <strong>DeepSeekMath-V2</strong> compare to other mathematical AI systems? Here's a breakdown of the key differences:
              </p>
              
              <div className="overflow-x-auto my-6">
                <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-md">
                  <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Model</th>
                      <th className="px-4 py-3 text-left font-semibold">Parameters</th>
                      <th className="px-4 py-3 text-left font-semibold">Self-Verification</th>
                      <th className="px-4 py-3 text-left font-semibold">Open Source</th>
                      <th className="px-4 py-3 text-left font-semibold">IMO Performance</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="bg-green-50 border-b">
                      <td className="px-4 py-3 font-semibold">DeepSeekMath-V2</td>
                      <td className="px-4 py-3">685B</td>
                      <td className="px-4 py-3 text-green-600 font-semibold">✓ Yes</td>
                      <td className="px-4 py-3 text-green-600 font-semibold">✓ Apache 2.0</td>
                      <td className="px-4 py-3 font-semibold">Gold Level</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3">DeepThink (DeepMind)</td>
                      <td className="px-4 py-3">Unknown</td>
                      <td className="px-4 py-3 text-green-600">✓ Yes</td>
                      <td className="px-4 py-3 text-red-600">✗ Closed</td>
                      <td className="px-4 py-3">Gold Level</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3">GPT-4</td>
                      <td className="px-4 py-3">~1.8T (est.)</td>
                      <td className="px-4 py-3 text-red-600">✗ No</td>
                      <td className="px-4 py-3 text-red-600">✗ Closed</td>
                      <td className="px-4 py-3">Silver Level</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3">Claude 3.5</td>
                      <td className="px-4 py-3">Unknown</td>
                      <td className="px-4 py-3 text-red-600">✗ No</td>
                      <td className="px-4 py-3 text-red-600">✗ Closed</td>
                      <td className="px-4 py-3">Silver Level</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Llama 4</td>
                      <td className="px-4 py-3">405B</td>
                      <td className="px-4 py-3 text-red-600">✗ No</td>
                      <td className="px-4 py-3 text-green-600">✓ Open</td>
                      <td className="px-4 py-3">Bronze Level</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                The combination of IMO Gold-level performance, self-verification capabilities, and full open-source availability makes <strong>DeepSeekMath-V2</strong> unique in the current landscape of mathematical AI.
              </p>
            </section>

            <section id="getting-started">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">9</span>
                Getting Started with DeepSeekMath-V2
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>DeepSeekMath-V2</strong> is available on Hugging Face under the Apache 2.0 license, making it accessible for both research and commercial applications:
              </p>
              
              <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-blue-600" />
                    Quick Start Resources
                  </h3>
                  <div className="space-y-4">
                    <a 
                      href="https://huggingface.co/deepseek-ai/DeepSeek-Math-V2" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group"
                    >
                      <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">🤗</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-blue-600">DeepSeekMath-V2 on Hugging Face</p>
                        <p className="text-sm text-gray-600">Model weights, documentation, and examples</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                    </a>
                    
                    <a 
                      href="https://github.com/deepseek-ai/DeepSeek-V3.2-Exp" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Code className="h-6 w-6 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-blue-600">GitHub Repository</p>
                        <p className="text-sm text-gray-600">Inference support and implementation guides</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                    </a>
                    
                    <a 
                      href="https://chat.deepseek.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Brain className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-blue-600">Try DeepSeek Chat</p>
                        <p className="text-sm text-gray-600">Experience DeepSeek's AI capabilities online</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                    </a>
                  </div>
                </CardContent>
              </Card>
              
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">System Requirements</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Due to its 685 billion parameter size, running DeepSeekMath-V2 locally requires significant computational resources. For most users, accessing the model through cloud APIs or DeepSeek's hosted services will be the most practical option.
              </p>
            </section>

            <section id="faq">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-full text-lg font-bold">10</span>
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-4 mt-6">
                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> What is DeepSeekMath-V2?
                    </h3>
                    <p className="text-gray-700">
                      <strong>DeepSeekMath-V2</strong> is a 685 billion parameter AI model developed by DeepSeek that specializes in self-verifiable mathematical reasoning. It achieves gold-level scores on IMO 2025, CMO 2024, and a near-perfect 118/120 on Putnam 2024. Unlike traditional math AI that optimizes for correct answers, it focuses on generating rigorous, verifiable proofs.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> What makes DeepSeekMath-V2 different from GPT-4 or Claude for math?
                    </h3>
                    <p className="text-gray-700">
                      While GPT-4 and Claude optimize for correct final answers, <strong>DeepSeekMath-V2</strong> focuses on self-verifiable reasoning. It can verify the comprehensiveness and rigor of its mathematical proofs, identify errors in its own work, and produce step-by-step derivations suitable for formal theorem proving—capabilities that answer-focused models lack.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> Is DeepSeekMath-V2 open source?
                    </h3>
                    <p className="text-gray-700">
                      Yes! DeepSeekMath-V2 is released under the <strong>Apache License 2.0</strong>, making it freely available for both research and commercial use. The model weights are available on Hugging Face, and inference code is available on GitHub.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> How does the self-verification work?
                    </h3>
                    <p className="text-gray-700">
                      The model is trained with a separate LLM-based verifier that checks proofs for logical consistency, completeness, and rigor. The proof generator uses this verifier as a reward model and is incentivized to identify and fix issues before finalizing. This creates a self-improving feedback loop where the model reviews its own work like a human mathematician would.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> What are IMO, Putnam, and CMO?
                    </h3>
                    <p className="text-gray-700">
                      <strong>IMO</strong> (International Mathematical Olympiad) is the world's most prestigious high school math competition. <strong>Putnam</strong> is North America's top university math competition where the median score is typically 0/120. <strong>CMO</strong> (China Mathematical Olympiad) is China's national olympiad for selecting the IMO team. Gold-level performance on these puts DeepSeekMath-V2 among the world's best mathematical reasoners.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <span className="text-blue-600">Q:</span> Can I run DeepSeekMath-V2 locally?
                    </h3>
                    <p className="text-gray-700">
                      With 685 billion parameters, running the full model locally requires significant GPU resources (multiple high-end GPUs with substantial VRAM). For most users, accessing the model through cloud APIs, Hugging Face inference endpoints, or DeepSeek's hosted services will be more practical.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Citation */}
            <Card className="mt-10 bg-gray-100 border-gray-300">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-gray-600" />
                  Citation
                </h3>
                <pre className="text-sm text-gray-700 overflow-x-auto bg-white p-4 rounded border">
{`@misc{deepseek-math-v2,
  author = {Zhihong Shao, Yuxiang Luo, Chengda Lu, 
            Z.Z. Ren, Jiewen Hu, Tian Ye, Zhibin Gou, 
            Shirong Ma, Xiaokang Zhang},
  title = {DeepSeekMath-V2: Towards Self-Verifiable 
           Mathematical Reasoning},
  year = {2025},
}`}
                </pre>
              </CardContent>
            </Card>

            {/* Author Info */}
            <Card className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    DS
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">DeepSeek AI Fan Site</h3>
                    <p className="text-gray-600 text-sm mb-2">Independent resource for DeepSeek AI enthusiasts</p>
                    <p className="text-gray-700 text-sm">
                      This article is part of our ongoing coverage of DeepSeek's AI breakthroughs. We're a fan site dedicated to tracking and explaining DeepSeek's contributions to the AI field. Not affiliated with Hangzhou DeepSeek Artificial Intelligence Co., Ltd.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </article>

          <div className="mt-12">
            <RelatedLinks />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              onClick={() => window.open('https://huggingface.co/deepseek-ai/DeepSeek-Math-V2', '_blank')}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View on Hugging Face
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/blog')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </div>
        </div>
      </main>

      <WaitlistDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </>
  );
};

export default DeepseekMathV2;
